import { getEasing } from './Easing';
import type GraphEngine from './GraphEngine';
import type { FocusOptions, FitOptions, MoveToOptions, Position } from '../types';

export default class Camera {
  _engine: GraphEngine;
  x: number;
  y: number;
  scale: number;
  _anim: boolean | null;
  _animRaf: number | null;

  constructor(engine: GraphEngine) {
    this._engine = engine;
    this.x = 0;
    this.y = 0;
    this.scale = 1;
    this._anim = null;
    this._animRaf = null;
  }

  DOMtoCanvas(domPos: Position): Position {
    const canvas = this._engine._canvas;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const cssX = domPos.x - rect.left;
    const cssY = domPos.y - rect.top;
    return {
      x: (cssX - canvas.width / dpr / 2) / this.scale + this.x,
      y: (cssY - canvas.height / dpr / 2) / this.scale + this.y,
    };
  }

  canvasToDOM(canvasPos: Position): Position {
    const canvas = this._engine._canvas;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (canvasPos.x - this.x) * this.scale + canvas.width / dpr / 2 + rect.left,
      y: (canvasPos.y - this.y) * this.scale + canvas.height / dpr / 2 + rect.top,
    };
  }

  moveTo(opts: MoveToOptions): void {
    const pos = opts.position || { x: this.x, y: this.y };
    const targetScale = opts.scale !== undefined ? opts.scale : this.scale;
    const anim = opts.animation;
    if (anim && anim.duration > 0) {
      this._animate(pos.x, pos.y, targetScale, anim.duration, anim.easingFunction || anim.easing || 'easeInOutQuad');
    } else {
      this.x = pos.x;
      this.y = pos.y;
      this.scale = targetScale;
      this._engine.markDirty();
    }
  }

  focus(nodeId: number | string, opts: FocusOptions = {}): void {
    const positions = this._engine.getPositions([nodeId]);
    const pos = positions[nodeId];
    if (!pos) return;
    const targetX = pos.x + (opts.offset?.x || 0) / (opts.scale || this.scale);
    const targetY = pos.y + (opts.offset?.y || 0) / (opts.scale || this.scale);
    const targetScale = opts.scale || this.scale;
    const anim = opts.animation;
    if (anim && anim.duration > 0) {
      this._animate(targetX, targetY, targetScale, anim.duration, anim.easing || anim.easingFunction || 'easeInOutQuad');
    } else {
      this.x = targetX;
      this.y = targetY;
      this.scale = targetScale;
      this._engine.markDirty();
    }
  }

  fit(opts: FitOptions = {}): void {
    const nodes = this._engine._nodes;
    if (nodes.length === 0) return;
    const physics = this._engine._physics;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (let i = 0; i < nodes.length; i++) {
      const idx = physics._idToIndex.get(nodes.get(nodes.getIds()[i]).id);
      if (idx === undefined) continue;
      const px = physics._pos[idx * 2];
      const py = physics._pos[idx * 2 + 1];
      if (px < minX) minX = px;
      if (py < minY) minY = py;
      if (px > maxX) maxX = px;
      if (py > maxY) maxY = py;
    }
    if (!isFinite(minX)) return;
    const canvas = this._engine._canvas;
    const dpr = window.devicePixelRatio || 1;
    const cw = canvas.width / dpr;
    const ch = canvas.height / dpr;
    const padding = 30;
    const rangeX = maxX - minX || 1;
    const rangeY = maxY - minY || 1;
    const scaleX = (cw - padding * 2) / rangeX;
    const scaleY = (ch - padding * 2) / rangeY;
    const targetScale = Math.min(scaleX, scaleY, 1.5);
    const targetX = (minX + maxX) / 2;
    const targetY = (minY + maxY) / 2;
    const anim = opts.animation;
    if (anim && anim.duration > 0) {
      this._animate(targetX, targetY, targetScale, anim.duration, anim.easingFunction || anim.easing || 'easeInOutQuad');
    } else {
      this.x = targetX;
      this.y = targetY;
      this.scale = targetScale;
      this._engine.markDirty();
    }
  }

  _animate(tx: number, ty: number, ts: number, duration: number, easingName: string): void {
    this._stopAnimation();
    const sx = this.x, sy = this.y, ss = this.scale;
    const easing = getEasing(easingName);
    const start = performance.now();
    const tick = () => {
      const elapsed = performance.now() - start;
      const t = Math.min(elapsed / duration, 1);
      const e = easing(t);
      this.x = sx + (tx - sx) * e;
      this.y = sy + (ty - sy) * e;
      this.scale = ss + (ts - ss) * e;
      this._engine.markDirty();
      if (t < 1) {
        this._animRaf = requestAnimationFrame(tick);
      } else {
        this._animRaf = null;
        this._anim = null;
      }
    };
    this._anim = true;
    this._animRaf = requestAnimationFrame(tick);
  }

  _stopAnimation(): void {
    if (this._animRaf !== null) {
      cancelAnimationFrame(this._animRaf);
      this._animRaf = null;
      this._anim = null;
    }
  }

  destroy(): void {
    this._stopAnimation();
  }
}
